import React from 'react';
import { ArrowRight, Truck, Wrench, Boxes, HardHat, TreePine, Warehouse, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const BusinessStorage = () => {
  const businessTypes = [
    {
      icon: <HardHat className="h-10 w-10 text-green-600" />,
      title: 'Construction Companies',
      description: 'Store equipment, materials, and vehicles between job sites'
    },
    {
      icon: <TreePine className="h-10 w-10 text-green-600" />,
      title: 'Landscaping Businesses',
      description: 'Keep mowers, trailers, and equipment secure and accessible'
    },
    {
      icon: <Truck className="h-10 w-10 text-green-600" />,
      title: 'Fleet Vehicles',
      description: 'Park work trucks, vans, and commercial vehicles'
    },
    {
      icon: <Wrench className="h-10 w-10 text-green-600" />,
      title: 'Contractors',
      description: 'Secure storage for tools, trailers, and materials'
    },
    {
      icon: <Boxes className="h-10 w-10 text-green-600" />,
      title: 'Equipment Rental',
      description: 'Store inventory and maintain rental fleet'
    },
    {
      icon: <Warehouse className="h-10 w-10 text-green-600" />,
      title: 'General Business',
      description: 'Overflow storage for inventory and supplies'
    }
  ];

  const benefits = [
    'Large open spaces for equipment',
    'Easy load and unload access',
    'No tight corners or narrow aisles',
    'Commercial accounts welcome',
    'Month-to-month flexibility',
    '24/7 access for your schedule',
    'Gravel surface for all weather access',
    'Highway 82 location for quick access'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Business & Equipment Storage in Bells, TX | VD Storage Bells"
        description="Secure outdoor storage for commercial vehicles, construction equipment, and business inventory in Bells, TX. 24/7 access, month-to-month rentals, Highway 82 location."
        canonical="/business-storage-bells-tx"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="business-storage-title">
            Business & Equipment Storage in Bells, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Secure outdoor storage for commercial vehicles, equipment, and inventory
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              VD Storage Bells provides affordable outdoor storage solutions for businesses across North Texas.
              Whether you're a contractor needing secure equipment storage, a landscaping company looking for
              vehicle parking, or any business requiring extra space, we have you covered.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our gravel lots offer the flexibility and space businesses need without the high costs
              of traditional warehousing. Park your fleet vehicles, store construction equipment, or keep your
              business inventory safe and accessible 24/7.
            </p>
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For These Businesses
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {businessTypes.map((type, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md">
                <div className="mb-4">{type.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{type.title}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Businesses Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Businesses Choose Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Large Spaces</h3>
              <p className="text-gray-600 mb-4">
                12x30 and 14x40 spaces perfect for equipment, vehicles, and materials
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Flexible Terms</h3>
              <p className="text-gray-600 mb-4">
                Month-to-month rentals with no long-term commitment required
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">24/7 Access</h3>
              <p className="text-gray-600 mb-4">
                Access your equipment and vehicles anytime, day or night
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Get secure, affordable storage for your equipment and vehicles today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg btn-primary"
              data-testid="contact-button"
            >
              Get Business Pricing
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/storage-sizes"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="sizes-button"
            >
              View Space Sizes
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessStorage;
